// Pure, runtime color math for coach white-labeling (#3707).
//
// The app's normal color work happens at compile time in Sass
// (lighten()/darken() in client/src/theme). But a coach picks ONE accent hex
// at runtime, and from that one color we must derive a whole shade family plus
// a contrast-safe text color — there is no Sass at runtime, so we do the math
// here. This module has no Angular / Firebase / browser dependencies so it can
// be unit-tested on its own (see color-math.test.ts).
//
// All "amount" values below are fractions in the range 0..1. lighten mixes the
// color toward white by that fraction; darken mixes it toward black. This is
// the standard tint/shade mix and is easy to reason about.

export interface Rgb {
  r: number; // 0..255
  g: number; // 0..255
  b: number; // 0..255
}

/**
 * The full set of brand colors derived from one accent hex. Every field is a
 * `#RRGGBB` string. The runtime theme service writes one
 * `--cs-brand-*-override` CSS variable per field.
 */
export interface BrandPalette {
  primary: string;  // the accent itself
  hover: string;    // one step lighter than primary (mirrors $blue-500 -> -400)
  active: string;   // one step darker than primary (mirrors $blue-500 -> -600)
  tint: string;     // dark, low-luminance wash for selected-row backgrounds
  link: string;     // lightened accent, legible on the dark app background
  linkHover: string; // a touch lighter than link, for link hover states
  onAccent: string; // text color that sits on the accent fill (white or dark ink)
}

// The dark ink used for on-accent text when the accent is light. Matches the
// app's $text-inverse ($gray-900) so light-on-accent text looks native.
export const ON_ACCENT_DARK_INK = '#0B0E11';
export const ON_ACCENT_LIGHT = '#FFFFFF';

// WCAG 2.x contrast threshold for normal text (4.5:1). The coach Branding page
// warns when the best achievable on-accent contrast falls below AA.
export const WCAG_AA_CONTRAST = 4.5;

// Amounts chosen to mirror the existing MH blue ramp relationship:
//   $color-primary $blue-500 -> hover $blue-400 (lighter) -> active $blue-600 (darker).
const HOVER_LIGHTEN = 0.18;
const ACTIVE_DARKEN = 0.18;
// link is a touch lighter than hover so it stays legible as text on the dark
// app background (mirrors MH, where $color-link is the lighter $blue-400).
const LINK_LIGHTEN = 0.24;
// link hover is lighter still, mirroring MH's $color-link -> $color-link-hover
// step ($blue-400 -> the lighter $blue-300).
const LINK_HOVER_LIGHTEN = 0.36;
// tint is a heavy darken so even a bright accent becomes a dark, low-luminance
// wash — matching the existing dark $background-highlight (#023B7D) used for
// selected rows. Never a near-white tint, because the UI is dark-mode only.
const TINT_DARKEN = 0.6;

/**
 * Parse and validate a hex color. Accepts `#RGB` and `#RRGGBB` (with or
 * without a leading `#`). Returns the channel values, or null when the input
 * is not a valid hex color. Callers treat null as "fall back to the MH
 * default".
 */
export function parseHex(hex: string | null | undefined): Rgb | null {
  if (!hex) return null;
  let body = hex.trim();
  if (body.startsWith('#')) body = body.slice(1);
  // expand shorthand #RGB -> #RRGGBB
  if (body.length === 3) {
    body = body[0] + body[0] + body[1] + body[1] + body[2] + body[2];
  }
  if (body.length !== 6) return null;
  if (!/^[0-9a-fA-F]{6}$/.test(body)) return null;
  return {
    r: parseInt(body.slice(0, 2), 16),
    g: parseInt(body.slice(2, 4), 16),
    b: parseInt(body.slice(4, 6), 16),
  };
}

// clamp a channel to 0..255 and round to a whole number.
function clampChannel(value: number): number {
  if (value < 0) return 0;
  if (value > 255) return 255;
  return Math.round(value);
}

function toHexChannel(value: number): string {
  return clampChannel(value).toString(16).padStart(2, '0').toUpperCase();
}

/** Format channel values back into a `#RRGGBB` string. */
export function rgbToHex({ r, g, b }: Rgb): string {
  return '#' + toHexChannel(r) + toHexChannel(g) + toHexChannel(b);
}

/**
 * WCAG relative luminance of a color (0 for black, 1 for white). Used by the
 * contrast-ratio math below.
 */
export function relativeLuminance({ r, g, b }: Rgb): number {
  const channel = (c: number): number => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/**
 * WCAG contrast ratio between two colors, always >= 1. A ratio of 21 is the
 * maximum (pure black vs pure white). 4.5 is the AA threshold for normal text.
 */
export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Mix a hex color toward white by `amount` (0..1). Returns a `#RRGGBB` string.
 * Callers should pass already-validated hex; if the input does not parse we
 * return it unchanged so a bad value never throws in the middle of a render.
 */
export function lighten(hex: string, amount: number): string {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  return rgbToHex({
    r: rgb.r + (255 - rgb.r) * amount,
    g: rgb.g + (255 - rgb.g) * amount,
    b: rgb.b + (255 - rgb.b) * amount,
  });
}

/** Mix a hex color toward black by `amount` (0..1). Returns a `#RRGGBB` string. */
export function darken(hex: string, amount: number): string {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  return rgbToHex({
    r: rgb.r * (1 - amount),
    g: rgb.g * (1 - amount),
    b: rgb.b * (1 - amount),
  });
}

/**
 * The best contrast ratio achievable for text on top of the accent, choosing
 * between white and the dark ink. The coach Branding page warns when this is
 * below WCAG AA.
 */
export function onAccentContrastRatio(hex: string): number {
  const accent = parseHex(hex);
  if (!accent) return 0;
  const whiteContrast = contrastRatio(accent, parseHex(ON_ACCENT_LIGHT)!);
  const darkContrast = contrastRatio(accent, parseHex(ON_ACCENT_DARK_INK)!);
  return Math.max(whiteContrast, darkContrast);
}

/**
 * Pick the text color to place on top of the accent fill: white or the dark
 * ink, whichever has the higher WCAG contrast against the accent itself. This
 * is the contrast-safety core — it guarantees readable text on any accent the
 * coach picks, even if the accent itself is a poor choice.
 */
export function onAccentTextColor(hex: string): string {
  const accent = parseHex(hex);
  if (!accent) return ON_ACCENT_LIGHT;
  const whiteContrast = contrastRatio(accent, parseHex(ON_ACCENT_LIGHT)!);
  const darkContrast = contrastRatio(accent, parseHex(ON_ACCENT_DARK_INK)!);
  return whiteContrast >= darkContrast ? ON_ACCENT_LIGHT : ON_ACCENT_DARK_INK;
}

/**
 * Derive the full brand palette from one accent hex. Returns null on invalid
 * input so callers fall back to the MH default everywhere. Derived values are
 * never persisted — they are recomputed at runtime, so these rules can change
 * with no data migration.
 */
export function deriveBrandPalette(hex: string | null | undefined): BrandPalette | null {
  const accent = parseHex(hex ?? null);
  if (!accent) return null;
  const primary = rgbToHex(accent);
  return {
    primary,
    hover: lighten(primary, HOVER_LIGHTEN),
    active: darken(primary, ACTIVE_DARKEN),
    tint: darken(primary, TINT_DARKEN),
    link: lighten(primary, LINK_LIGHTEN),
    linkHover: lighten(primary, LINK_HOVER_LIGHTEN),
    onAccent: onAccentTextColor(primary),
  };
}
